import React, { useMemo, useRef, useState, useEffect, RefObject } from "react";
import { useAppStore } from "@/stores/appStore";
import { useFieldConfig } from "@/hooks/useFieldConfig";
import { StatusDot } from "@/components/ui/Badge";
import type { Initiative, SubProduct } from "@/types";
import { LinkIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { ICON_MAP } from "@/components/modals/IconPicker";
import { formatMonthLabel } from "@/utils/monthLabel";
import { MonthLabel } from "@/components/ui/MonthLabel";
import { isDateMode } from "@/components/timeline/TimelineWeekView";
import { formatActualDate } from "@/utils/dateRange";
import { INITIATIVE_TITLE_LINK_CLASS } from "@/utils/uiConventions";
import { ClassificationFooter } from "@/components/ui/ClassificationFooter";

// Component for truncated title with tooltip (only shows when actually truncated).
// className/onClick let callers apply the app-wide "clickable initiative title" affordance
// (see the design note in ListView.tsx) without duplicating the truncation/tooltip logic.
function TruncatedTitle({
  title,
  className,
  onClick,
}: {
  title: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    const el = titleRef.current;
    if (el && el.scrollWidth > el.clientWidth) setShowTooltip(true);
  };

  return (
    <div className="relative min-w-0 flex-1">
      <span
        ref={titleRef}
        className={`truncate block ${className || ''}`}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {title}
      </span>
      {showTooltip && (
        <div className="absolute top-full left-0 mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded z-50 max-w-md break-words">
          {title}
        </div>
      )}
    </div>
  );
}

// Renders up to 2 tags inline; any remaining collapse into a hover "+N" pill so a
// tag-heavy row can't widen the Tags column for every record.
function TagList({ tags }: { tags?: Initiative["tags"] }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const valid = (tags || []).filter((t) => t.tag);
  const MAX_VISIBLE = 2;
  const visible = valid.slice(0, MAX_VISIBLE);
  const hidden = valid.slice(MAX_VISIBLE);

  const renderTag = (initTag: NonNullable<Initiative["tags"]>[number]) => {
    const IconComponent = initTag.tag?.icon ? ICON_MAP[initTag.tag.icon] : null;
    return (
      <div key={initTag.tagId} className="flex items-center gap-1.5 text-xs text-text-secondary">
        {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
        <span>{initTag.tag?.name}</span>
      </div>
    );
  };

  return (
    <div className="flex items-center gap-2">
      {visible.map(renderTag)}
      {hidden.length > 0 && (
        <div className="relative flex-shrink-0">
          <span
            className="text-xs text-text-secondary bg-gray-100 rounded-full px-1.5 py-0.5 cursor-default"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            +{hidden.length}
          </span>
          {showTooltip && (
            <div className="absolute top-full right-0 mt-1 px-2 py-1.5 bg-gray-900 text-white rounded z-50 flex flex-col gap-1.5 whitespace-nowrap">
              {hidden.map((initTag) => {
                const IconComponent = initTag.tag?.icon ? ICON_MAP[initTag.tag.icon] : null;
                return (
                  <div key={initTag.tagId} className="flex items-center gap-1.5 text-xs">
                    {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                    <span>{initTag.tag?.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MONTH_TO_Q: Record<string, string> = {
  Jan: 'Q1', Feb: 'Q1', Mar: 'Q1',
  Apr: 'Q2', May: 'Q2', Jun: 'Q2',
  Jul: 'Q3', Aug: 'Q3', Sep: 'Q3',
  Oct: 'Q4', Nov: 'Q4', Dec: 'Q4',
};

function withQuarterGroup(fullLabel: string): string {
  const parts = fullLabel.split(' ');
  if (parts.length !== 2) return fullLabel;
  const q = MONTH_TO_Q[parts[1]];
  return q ? `${parts[0]} ${q} ${parts[1]}` : fullLabel;
}

interface BatchListViewProps {
  initiatives?: Initiative[];
  containerRef?: RefObject<HTMLDivElement>;
  viewMode?: 'weekly' | 'monthly' | 'quarterly';
  selectedIds: Set<string>;
  onToggleOne: (id: string) => void;
  onToggleAll: (checked: boolean) => void;
}

export function BatchListView({
  initiatives: filteredInitiatives,
  containerRef,
  viewMode = 'monthly',
  selectedIds,
  onToggleOne,
  onToggleAll,
}: BatchListViewProps) {
  const { products, initiatives: allInitiatives, quarters, openViewInitiativeModal } = useAppStore();
  const fc = useFieldConfig();

  const initiatives = filteredInitiatives || allInitiatives;

  const isWeekly = viewMode === 'weekly';
  // Weekly shows only date-mode initiatives (exact dates); month-mode rows are hidden.
  const displayInitiatives = useMemo(
    () => (isWeekly ? initiatives.filter(isDateMode) : initiatives),
    [initiatives, isWeekly],
  );
  const hiddenMonthModeCount = initiatives.length - displayInitiatives.length;

  // Sort direction for the Start column (default earliest-first)
  const [startSortDir, setStartSortDir] = useState<'asc' | 'desc'>('asc');

  const quarterOrder = useMemo(() => {
    const m = new Map<string, number>();
    [...quarters]
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .forEach((q, i) => m.set(q.key, i));
    return m;
  }, [quarters]);

  const startVal = (init: Initiative) =>
    isWeekly
      ? (init.startDate ? new Date(init.startDate).getTime() : Number.MAX_SAFE_INTEGER)
      : (quarterOrder.get(init.startQuarter ?? '') ?? Number.MAX_SAFE_INTEGER);

  const initiativesBySubProduct = useMemo(() => {
    const grouped = new Map<string, Initiative[]>();
    displayInitiatives.forEach((init) => {
      const key = init.subProductId;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(init);
    });
    const dir = startSortDir === 'asc' ? 1 : -1;
    grouped.forEach((arr) =>
      arr.sort((a, b) => dir * (startVal(a) - startVal(b)) || (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()))
    );
    return grouped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayInitiatives, quarterOrder, startSortDir, isWeekly]);

  const subProductsByProduct = useMemo(() => {
    const grouped = new Map<string, SubProduct[]>();
    products.forEach((product) => {
      if (product.subProducts) grouped.set(product.id, product.subProducts);
    });
    return grouped;
  }, [products]);

  const getProductInitiativeCount = (productId: string): number => {
    const subProducts = subProductsByProduct.get(productId) || [];
    return subProducts.reduce((count, sp) => count + (initiativesBySubProduct.get(sp.id)?.length || 0), 0);
  };

  const getQuarterLabel = (quarterKey: string) => {
    const quarter = quarters.find((q) => q.key === quarterKey);
    const label = quarter?.fullLabel || quarterKey;
    return formatMonthLabel(viewMode === 'quarterly' ? withQuarterGroup(label) : label);
  };

  // Header select-all state across all currently displayed records
  const allIds = useMemo(() => displayInitiatives.map((i) => i.id), [displayInitiatives]);
  const selectedVisible = allIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = allIds.length > 0 && selectedVisible === allIds.length;
  const someSelected = selectedVisible > 0 && !allSelected;
  const headerRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someSelected;
  }, [someSelected]);

  if (isWeekly && displayInitiatives.length === 0) {
    return (
      <div ref={containerRef} className="h-full flex flex-col items-center justify-center text-center px-6">
        <p className="text-text-secondary text-sm max-w-xl">
          Found {hiddenMonthModeCount} {hiddenMonthModeCount === 1 ? "record" : "records"} — switch to Monthly or Quarterly to see them.
          <br />
          Weekly shows only initiatives entered with exact dates (Date mode).
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-thin">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-background-secondary">
            <tr>
              <th className="w-10 text-left py-2 px-4 border-b border-gray-200">
                <input
                  ref={headerRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  className="h-4 w-4 cursor-pointer"
                  title="Select all"
                />
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                {fc.label('initiative')} Name
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                {fc.label('status')}
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-sm">
                <button
                  type="button"
                  onClick={() => setStartSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  aria-sort={startSortDir === 'asc' ? 'ascending' : 'descending'}
                  title="Sort by start"
                  className="inline-flex items-center gap-1 text-blue-600 transition-colors"
                >
                  {viewMode === 'quarterly' ? 'Start Quarter' : fc.label('start')}
                  {startSortDir === 'asc' ? (
                    <ChevronUpIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  )}
                </button>
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                {viewMode === 'quarterly' ? 'End Quarter' : fc.label('end')}
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                {fc.label('owner')}
              </th>
              <th className="text-left py-2 px-4 border-b border-gray-200 font-semibold text-text-primary text-sm">
                {fc.label('tags')}
              </th>
            </tr>
          </thead>

          <tbody>
            {products.map((product) => {
              const subProducts = subProductsByProduct.get(product.id) || [];
              const productInitiativeCount = getProductInitiativeCount(product.id);
              if (productInitiativeCount === 0) return null;

              return (
                <React.Fragment key={product.id}>
                  <tr className="bg-blue-50/50 border-b border-gray-100">
                    <td colSpan={7} className="py-2 px-4 font-semibold text-text-primary text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {product.icon && ICON_MAP[product.icon] && (
                          <span style={{ color: product.iconColor || '#6B7280' }}>
                            {(() => {
                              const IconComponent = ICON_MAP[product.icon];
                              return <IconComponent className="w-4 h-4 flex-shrink-0" />;
                            })()}
                          </span>
                        )}
                        <span className="truncate" title={product.name}>{product.name}</span>
                        <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                          {productInitiativeCount}
                        </span>
                      </div>
                    </td>
                  </tr>

                  {subProducts.map((subProduct) => {
                    const subProductInitiatives = initiativesBySubProduct.get(subProduct.id) || [];
                    if (subProductInitiatives.length === 0) return null;

                    return (
                      <React.Fragment key={subProduct.id}>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <td colSpan={7} className="py-1.5 px-3 pl-8 font-medium text-sm text-text-secondary">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate" title={subProduct.name}>{subProduct.name}</span>
                              <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                {subProductInitiatives.length}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {subProductInitiatives.map((initiative) => {
                          const classification = initiative.classification;
                          const status = initiative.status;
                          const outgoingCount = initiative.dependencies?.length || 0;
                          const reverseCount = (initiative as any)._count?.dependents || 0;
                          const hasDependencies = outgoingCount > 0 || reverseCount > 0;
                          const isChecked = selectedIds.has(initiative.id);

                          return (
                            <tr
                              key={initiative.id}
                              className={`border-b border-gray-100 transition-colors cursor-pointer group ${
                                isChecked ? 'bg-blue-50/60 hover:bg-blue-50' : 'hover:bg-surface-hover'
                              }`}
                              onClick={() => onToggleOne(initiative.id)}
                            >
                              {/* Selection checkbox */}
                              <td className="w-10 py-2 px-4" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => onToggleOne(initiative.id)}
                                  className="h-4 w-4 cursor-pointer"
                                />
                              </td>

                              {/* Initiative Name */}
                              <td className="py-2 px-4 max-w-md">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div
                                    className="w-1 h-8 rounded-full flex-shrink-0"
                                    style={{
                                      backgroundColor: fc.isVisible('classification')
                                        ? (classification?.color || "#3B82F6")
                                        : "#D1D5DB",
                                    }}
                                  />
                                  {initiative.isStarred && (
                                    <StarSolid className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                                  )}
                                  <TruncatedTitle
                                    title={initiative.title}
                                    className={INITIATIVE_TITLE_LINK_CLASS}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openViewInitiativeModal(initiative);
                                    }}
                                  />
                                  {hasDependencies && (
                                    <LinkIcon className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                                  )}
                                </div>
                              </td>

                              {/* Status */}
                              <td className="py-2 px-4 whitespace-nowrap">
                                {status && (
                                  <div className="flex items-center gap-2">
                                    <StatusDot color={status.color} />
                                    <span className="text-sm text-text-secondary">{status.name}</span>
                                  </div>
                                )}
                              </td>

                              {/* Start */}
                              <td className="py-2 px-4 whitespace-nowrap">
                                <span className="text-sm text-text-secondary">
                                  {isWeekly
                                    ? formatActualDate(initiative.startDate)
                                    : <MonthLabel value={getQuarterLabel(initiative.startQuarter ?? '')} />}
                                </span>
                              </td>

                              {/* End */}
                              <td className="py-2 px-4 whitespace-nowrap">
                                <span className="text-sm text-text-secondary">
                                  {isWeekly
                                    ? (initiative.endDate ? formatActualDate(initiative.endDate) : "-")
                                    : (initiative.endQuarter ? <MonthLabel value={getQuarterLabel(initiative.endQuarter)} /> : "-")}
                                </span>
                              </td>

                              {/* Owner */}
                              <td className="py-2 px-4 whitespace-nowrap">
                                <span className="text-sm text-text-secondary">
                                  {(initiative as any).user?.name || '—'}
                                </span>
                              </td>

                              {/* Tags */}
                              <td className="py-2 px-4 whitespace-nowrap">
                                <TagList tags={initiative.tags} />
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ClassificationFooter />
    </div>
  );
}
